/** フォームで使われる判定の値。モデル側の enum に合わせる。 */
export const APPROVE_VALUE = "承認";
export const REJECT_VALUE = "差し戻し";

type JsonObject = Readonly<Record<string, unknown>>;

/**
 * 承認フォームの JSON Schema から提出用の payload を組み立てる。
 *
 * 判定の項目名はタスクごとに違う（manager_decision / director_decision）ため、
 * 名前を決め打ちにせず「指定した判定値を選択肢に持つ項目」を探す。
 * コメント項目は判定項目と同じ接頭辞（manager_ / director_）で対応付ける。
 *
 * 判定項目が見つからない、または選択肢に値が無い場合は null を返す。
 * 一括承認では null のタスクを対象外にして、勝手な値を送らないようにする。
 */
export function buildApprovalPayload(
  schema: unknown,
  comment?: string,
  decision: string = APPROVE_VALUE,
): Record<string, unknown> | null {
  if (!isObject(schema)) return null;

  const properties = schema.properties;
  if (!isObject(properties)) return null;

  const decisionKey = Object.keys(properties).find((key) => {
    const property = properties[key];
    if (!isObject(property)) return false;
    return (
      Array.isArray(property.enum) &&
      property.enum.some((choice) => choice === decision)
    );
  });

  if (decisionKey === undefined) return null;

  const payload: Record<string, unknown> = { [decisionKey]: decision };

  if (comment !== undefined && comment !== "") {
    const commentKey = findCommentKey(properties, decisionKey);
    if (commentKey !== undefined) payload[commentKey] = comment;
  }

  return payload;
}

/** manager_decision → manager_comment のように、同じ接頭辞のコメント項目を探す。 */
function findCommentKey(
  properties: JsonObject,
  decisionKey: string,
): string | undefined {
  const prefix = decisionKey.replace(/decision$/, "");

  return Object.keys(properties).find(
    (key) => key.startsWith(prefix) && key.endsWith("comment"),
  );
}

function isObject(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
